import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authClient from '../client/authClient';
import apiService from '../services/api';
import { getBackendErrorMessage, getFirebaseErrorMessage, isVerificationLinkError } from '../utils/authUtils';
import { DEFAULT_POST_LOGIN_PATH } from '../utils/authRoutes';
import { InvitePrefill, normalizeEmail, readInvitePrefill } from '../utils/invitePrefill';
import { checkPasswordRules, MIN_PASSWORD_LENGTH } from '../utils/passwordRules';
import { usePasswordReset } from './usePasswordReset';
import { useVerifyEmailStep, VerifyEmailArrival } from './useVerifyEmailStep';

export type LoginStep = 'email' | 'password' | 'create-password' | 'verify-email' | 'check-inbox';

/** The backend error code (`POST /auth/login`, 403) for an unverified address. */
const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

const getBackendErrorCode = (err: any): string | undefined =>
    err?.response?.data?.error?.code;
/**
 * 'invited' = an invite is waiting; 'domain' = no invite, but the email is on a
 * domain an organization has opened (provisioned into it at first login). Both
 * take the create-password step — or the password step when the address
 * already has one — and only the copy differs.
 */
export type UserStatus = 'invited' | 'domain' | 'registered' | 'not-found' | null;

export const useLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { login, signup, loginWithGoogle } = useAuth();

    // Fresh login lands on the dashboard. A real deep link captured by
    // PrivateRoute (location.state.from) is respected after auth — but the
    // bare root "/" is the marketing HomePage, not a destination anyone
    // deep-linked to, so it falls through to the dashboard.
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    const postLoginPath =
        from && from.pathname !== '/' ? `${from.pathname}${from.search || ''}` : DEFAULT_POST_LOGIN_PATH;

    // Step management
    const [step, setStep] = useState<LoginStep>('email');
    const [userStatus, setUserStatus] = useState<UserStatus>(null);

    // Form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');

    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Resend on the 'verify-email' step
    const verification = useVerifyEmailStep({ email, password, setError });

    // Forgot password (the 'check-inbox' step)
    const passwordReset = usePasswordReset({ email, setError, onLinkSent: () => setStep('check-inbox') });

    /** Park the person on "check your inbox", showing `error` ('' for none). */
    const showVerifyEmailStep = (arrival: VerifyEmailArrival, error = '') => {
        setError(error);
        verification.enter(arrival);
        setStep('verify-email');
    };

    /**
     * The backend refused a password sign-in as unverified (and the Firebase
     * session is already signed out): "check your inbox", with a fresh link
     * sent as the step opens.
     */
    const showUnverifiedStep = () => {
        setError(getBackendErrorMessage(EMAIL_NOT_VERIFIED));
        setStep('verify-email');
        return verification.enterAndSend();
    };

    const resetState = () => {
        setStep('email');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setError('');
        setUserStatus(null);
        verification.clear();
        passwordReset.clear();
    };

    /**
     * The email step's only real work: ask the backend what this address is
     * and move to the matching step. Takes the address explicitly so the
     * `?email=` prefill can run it before React has flushed `setEmail`.
     */
    const runEmailPrecheck = async (address: string) => {
        try {
            setError('');
            setIsLoading(true);

            const result = await authClient.checkUserStatus(address);

            if (result.status === 'EXISTING_USER') {
                setUserStatus('registered');
                setStep('password');
            } else if (result.status === 'INVITED_USER' || result.status === 'DOMAIN_USER') {
                setUserStatus(result.status === 'DOMAIN_USER' ? 'domain' : 'invited');
                // Signed up but not yet joined (e.g. never clicked the
                // verification link): the password exists, so ask for it.
                setStep(result.hasPassword ? 'password' : 'create-password');
                if (result.status === 'INVITED_USER' && !result.hasPassword) prefillInviteName(address);
            }
        } catch (err: any) {
            // Check for structured backend error
            const backendError = err.response?.data?.error;
            if (backendError && backendError.code) {
                if (backendError.code === 'USER_NOT_FOUND_NO_INVITE') {
                    setUserStatus('not-found');
                }
                setError(getBackendErrorMessage(backendError.code));
            } else {
                setError(apiService.getErrorMessage(err));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await runEmailPrecheck(email);
    };

    /**
     * Invitation links carry the invited address (`/login?email=...&name=...`).
     * Prefill the email field with it, once: a person who edits the field is
     * never second-guessed by a re-run. The name waits in `invite` until the
     * precheck opens create-password for that same address.
     */
    const prefillHandled = useRef(false);
    const invite = useRef<InvitePrefill | null>(null);
    useEffect(() => {
        if (prefillHandled.current) return;

        const prefill = readInvitePrefill(searchParams);
        if (!prefill) return;

        prefillHandled.current = true;
        invite.current = prefill;
        // Prefill only — never submit on the person's behalf. An invitee may
        // prefer "Sign in with Google" over the email path, so the choice of
        // how to continue stays theirs.
        setEmail(prefill.email);
    }, [searchParams]);

    /**
     * The invite's name is only offered to the invite's own address, and only
     * into an empty field — it never replaces what the person typed. Back to
     * the email step already clears the field (`resetState`), so a different
     * address arrives at an empty one.
     */
    const prefillInviteName = (address: string) => {
        const name = invite.current?.name;
        if (!name || invite.current?.email !== normalizeEmail(address)) return;
        setName((current) => current || name);
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setError('');
            setIsLoading(true);

            await login(email, password);
            navigate(postLoginPath, { replace: true });
        } catch (err: any) {
            if (getBackendErrorCode(err) === EMAIL_NOT_VERIFIED) return showUnverifiedStep();

            const errorMessage = err.code ? getFirebaseErrorMessage(err.code) : err.message;
            setError(errorMessage || 'Failed to sign in');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreatePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Same rules as the reset page, from the one definition both share.
        const rules = checkPasswordRules(password, confirmPassword, email);
        if (!rules.matches) {
            return setError('Passwords do not match');
        }
        if (!rules.longEnough) {
            return setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
        }
        if (!rules.notEmail) {
            return setError("Your password can't be your email address");
        }

        try {
            setError('');
            setIsLoading(true);

            // signup() requests the verification link and leaves the app signed
            // out — no /auth/login until the person comes back verified.
            await signup(email, password, name);
            showVerifyEmailStep('sent');
        } catch (err: any) {
            // The account exists; only its link failed. Resend is the way on.
            if (isVerificationLinkError(err)) return showVerifyEmailStep('failed', err.message);

            // Auto-login fallback if user already exists in Firebase
            if (err.code === 'auth/email-already-in-use') {
                try {
                    await login(email, password);
                    navigate(postLoginPath, { replace: true });
                    return; // Exit successfully
                } catch (loginErr: any) {
                    if (getBackendErrorCode(loginErr) === EMAIL_NOT_VERIFIED) return showUnverifiedStep();
                    // Otherwise show the original sign-up error below.
                }
            }

            const errorMessage = err.code ? getFirebaseErrorMessage(err.code) : err.message;
            setError(errorMessage || 'Failed to create account');
        } finally {
            setIsLoading(false);
        }
    };

    const backToPassword = () => {
        setError('');
        setStep('password');
    };

    const handleGoogleLogin = async () => {
        try {
            setError('');
            setIsLoading(true);

            const result = await loginWithGoogle();

            if (result && result.user && result.user.email) {
                try {
                    await authClient.checkUserStatus(result.user.email);
                } catch (err: any) {
                    if (err.response?.status === 403) {
                        setError('You need an invitation to access this application. Please contact your administrator.');
                        return;
                    }
                    throw err;
                }
            }

            navigate(postLoginPath, { replace: true });
        } catch (err: any) {
            const errorMessage = err.code ? getFirebaseErrorMessage(err.code) : err.message;
            setError(errorMessage || 'Failed to sign in with Google');
        } finally {
            setIsLoading(false);
        }
    };

    return {
        step,
        userStatus,
        email, setEmail,
        password, setPassword,
        confirmPassword, setConfirmPassword,
        name, setName,
        isLoading,
        error, setError,
        verification,
        passwordReset,
        postLoginPath,
        resetState,
        handleEmailSubmit,
        handlePasswordSubmit,
        handleCreatePasswordSubmit,
        backToPassword,
        handleGoogleLogin
    };
};
